/**
 * Allforwood - Blog Article Product Embedder
 * Intercepts standalone product links and plain text product URLs in the article content
 * and replaces them with dynamic, responsive, and interactive product cards (Wirecutter/Wired style).
 */
document.addEventListener('DOMContentLoaded', function() {
    const articleBody = document.querySelector('.gallery-article__rte.rte');
    if (!articleBody) return;

    // Scan all paragraph tags inside the article body
    const paragraphs = articleBody.querySelectorAll('p');
    
    paragraphs.forEach(paragraph => {
        // Normalize text content: replace non-breaking spaces (\u00a0) with standard spaces and trim
        const text = paragraph.textContent.replace(/\u00a0/g, ' ').trim();
        
        let handle = null;
        const originalParagraph = paragraph.cloneNode(true);
        const links = paragraph.querySelectorAll('a');

        if (links.length === 1) {
            // Case 1: Standalone link tag
            const link = links[0];
            const href = link.getAttribute('href');
            if (href) {
                const productMatch = href.match(/\/products\/([a-zA-Z0-9\-_]+)\/?(?:\?|$)/);
                const linkText = link.textContent.replace(/\u00a0/g, ' ').trim();
                
                // If paragraph text matches link text, it means it is a standalone link
                if (productMatch && text === linkText) {
                    handle = productMatch[1];
                }
            }
        } else if (links.length === 0) {
            // Case 2: Plain text product URL (if the merchant pasted a URL but didn't link it)
            // Matches: /products/handle or https://domain.com/products/handle (with optional trailing slash)
            const urlMatch = text.match(/^(?:https?:\/\/[a-zA-Z0-9\-\.]+)?\/products\/([a-zA-Z0-9\-_]+)\/?$/);
            if (urlMatch) {
                handle = urlMatch[1];
            }
        }

        // If a product handle is successfully parsed, replace paragraph with product card
        if (handle) {
            // Create a wrapper card in a loading state
            const cardContainer = document.createElement('div');
            cardContainer.className = 'editorial-product-block loading';
            cardContainer.setAttribute('data-handle', handle);
            
            // Replace standalone paragraph with card loader
            paragraph.replaceWith(cardContainer);

            // Fetch product information via Shopify AJAX API using custom JSON template
            fetch(`/products/${handle}?view=embed-json`)
                .then(response => {
                    if (!response.ok) throw new Error('Product details not found');
                    return response.json();
                })
                .then(product => {
                    renderEmbeddedProduct(cardContainer, product);
                })
                .catch(error => {
                    console.error(`Failed to embed product "${handle}":`, error);
                    // Gracefully fallback to original paragraph
                    cardContainer.replaceWith(originalParagraph);
                });
        }
    });

    /**
     * Renders the product card HTML once AJAX data is retrieved
     */
    function renderEmbeddedProduct(container, product) {
        // Remove loading class
        container.classList.remove('loading');
        
        // Price formatting using self-contained Shopify-standard formatMoney
        const formattedPrice = formatMoney(product.price);
        let comparePriceHtml = '';
        if (product.compare_at_price > product.price) {
            comparePriceHtml = `<span class="visually-hidden">Original price</span><s>${formatMoney(product.compare_at_price)}</s>`;
        }

        // SEC-09: fallback SVG local trung tính — không gọi picsum.photos (ảnh ngẫu nhiên bên thứ 3) trên store thật
        const PLACEHOLDER_SVG = 'data:image/svg+xml;utf8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400"><rect width="400" height="400" fill="#ece7df"/><path d="M140 250l50-60 40 45 30-35 50 50v30H140z" fill="#c9c0b2"/><circle cx="165" cy="155" r="18" fill="#c9c0b2"/></svg>');
        const imageUrl = product.featured_image || PLACEHOLDER_SVG;

        // Render badges HTML if present
        let badgesHtml = '';
        if (product.custom_badges && product.custom_badges.length > 0) {
            badgesHtml = `
                <div class="editorial-product-block__badges">
                    ${product.custom_badges.map(badge => `
                        <div class="custom-badge" style="background-color: ${badge.background_color || '#000'}; color: ${badge.text_color || '#fff'};">
                            ${badge.icon_svg ? `<span class="custom-badge__icon">${badge.icon_svg}</span>` : ''}
                            ${badge.label ? `<span class="custom-badge__label">${escapeHtml(badge.label)}</span>` : ''}
                        </div>
                    `).join('')}
                </div>
            `;
        }

        // Render card elements using custom editorial product block classes
        container.innerHTML = `
            <div class="editorial-product-block__image-container">
                <a href="${product.url}" class="editorial-product-block__image-link">
                    <img src="${imageUrl}" alt="${escapeHtml(product.title)}" loading="lazy" class="editorial-product-block__image">
                </a>
            </div>
            <div class="editorial-product-block__content">
                <h3 class="editorial-product-block__title">
                    <a href="${product.url}">${escapeHtml(product.title)}</a>
                </h3>
                
                ${badgesHtml}
                
                <div class="editorial-product-block__price-container">
                    <span class="editorial-product-block__price">
                        ${formattedPrice}
                    </span>
                    ${product.compare_at_price > product.price ? `
                        <span class="editorial-product-block__price-compare">
                            ${comparePriceHtml}
                        </span>
                    ` : ''}
                </div>
                
                <div class="editorial-product-block__actions">
                    <a href="${product.url}" class="editorial-product-block__button">
                        Shop Now
                    </a>
                </div>
            </div>
        `;
    }

    /**
     * Self-contained Shopify-standard formatMoney implementation
     */
    function formatMoney(cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
            const formatString = window.theme?.moneyFormat || window.Shopify.money_format || "${{amount}}";
            return window.Shopify.formatMoney(cents, formatString);
        }

        if (typeof cents === 'string') cents = cents.replace('.', '');
        let value = '';
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        const formatString = window.theme?.moneyFormat || window.Shopify?.money_format || "${{amount}}";

        function defaultOption(opt, def) {
            return (typeof opt == 'undefined' ? def : opt);
        }

        function formatWithDelimiters(number, precision, thousands, decimal) {
            precision = defaultOption(precision, 2);
            thousands = defaultOption(thousands, ',');
            decimal   = defaultOption(decimal, '.');

            if (isNaN(number) || number == null) { return 0; }

            number = (number/100.0).toFixed(precision);

            var parts   = number.split('.'),
                dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
                cents   = parts[1] ? (decimal + parts[1]) : '';

            return dollars + cents;
        }

        const match = formatString.match(placeholderRegex);
        if (!match) {
            // JS-15: store US — fallback USD, không phải ký hiệu đồng
            return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }

        switch(match[1]) {
            case 'amount':
                value = formatWithDelimiters(cents, 2);
                break;
            case 'amount_no_decimals':
                value = formatWithDelimiters(cents, 0);
                break;
            case 'amount_with_comma_separator':
                value = formatWithDelimiters(cents, 2, '.', ',');
                break;
            case 'amount_no_decimals_with_comma_separator':
                value = formatWithDelimiters(cents, 0, '.', ',');
                break;
            case 'amount_no_spaces':
                value = formatWithDelimiters(cents, 2).replace(' ', '');
                break;
        }

        return formatString.replace(placeholderRegex, value);
    }

    /**
     * Basic HTML escaping helper
     */
    function escapeHtml(str) {
        return str
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }
});
